<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader
      title="Data Attributes"
      subtitle="Reusable data primitives — write a SQL query once, then compose them into workflows."
    >
      <button class="btn-secondary" :disabled="isLoading" @click="fetchAttributes">
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="isLoading" />
        Refresh
      </button>
      <button class="btn-accent ml-2" @click="builderVisible = true">
        <font-awesome-icon :icon="['fas', 'plus']" />
        New Data Attribute
      </button>
    </ComponentHeader>

    <!-- Search: filters the loaded list by name, type, role, or column. -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
      <div class="relative flex-1">
        <font-awesome-icon
          :icon="['fas', 'magnifying-glass']"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by name, type, role, or column…"
          class="w-full border border-gray-300 rounded-md pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
        />
      </div>
      <span class="text-xs text-gray-400 tabular-nums shrink-0">
        {{ filteredAttributes.length }}<template v-if="searchQuery"> of {{ attributes.length }}</template>
      </span>
    </div>

    <table class="w-full divide-y divide-gray-100">
      <thead>
        <tr class="bg-gray-50/60">
          <th class="px-6 py-2.5 text-left text-xs font-semibold text-gray-500">Name</th>
          <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-32">Type</th>
          <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-28">Role</th>
          <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Columns</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-50">
        <tr v-for="da in filteredAttributes" :key="da.id" class="hover:bg-gray-50/70">
          <td class="px-6 py-2.5 align-middle">
            <div class="flex items-center gap-2">
              <font-awesome-icon :icon="['fas', 'table']" class="text-primary-500/70 text-xs shrink-0" />
              <span class="text-sm font-medium text-gray-900">{{ da.name }}</span>
            </div>
          </td>
          <td class="px-4 py-2.5 align-middle">
            <span class="text-xs text-gray-500">{{ da.attribute_type || '—' }}</span>
          </td>
          <td class="px-4 py-2.5 align-middle">
            <span v-if="da.role" class="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{{ da.role }}</span>
            <span v-else class="text-xs text-gray-300">—</span>
          </td>
          <td class="px-4 py-2.5 align-middle">
            <span class="font-mono text-[11px] text-slate-500 break-all">
              {{ (da.output_columns && da.output_columns.length) ? da.output_columns.join(', ') : '—' }}
            </span>
          </td>
        </tr>
        <!-- No matches for the current search (but attributes DO exist). -->
        <tr v-if="!isLoading && attributes.length && !filteredAttributes.length">
          <td colspan="4" class="px-6 py-14 text-center">
            <p class="text-sm text-gray-500">No data attributes match “{{ searchQuery }}”.</p>
          </td>
        </tr>
        <!-- No attributes at all. -->
        <tr v-if="!isLoading && !attributes.length">
          <td colspan="4" class="px-6 py-14 text-center">
            <div class="flex flex-col items-center gap-3">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <font-awesome-icon :icon="['fas', 'table']" class="text-lg" />
              </div>
              <p class="text-sm font-medium text-gray-700">No data attributes yet.</p>
              <button class="btn-accent mt-1" @click="builderVisible = true">
                <font-awesome-icon :icon="['fas', 'plus']" /> New Data Attribute
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="successMessage" class="px-6 py-2 text-xs text-green-dark">{{ successMessage }}</p>

    <DataAttributeBuilderModal
      :visible="builderVisible"
      @close="builderVisible = false"
      @created="onCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import DataAttributeBuilderModal from './DataAttributeBuilderModal.vue';

const ATTR_BASE = '/api/impact_analytics/attributes/';

export default {
  name: 'OverviewETLDataAttributes',

  components: { ComponentHeader, DataAttributeBuilderModal },

  data() {
    return {
      attributes: [],
      searchQuery: '',
      isLoading: false,
      builderVisible: false,
      successMessage: null,
    };
  },

  computed: {
    // Client-side filter over the loaded list — matches name, type, role, or any
    // output column (case-insensitive). Empty query returns everything.
    filteredAttributes() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.attributes;
      return this.attributes.filter((da) => {
        const cols = (da.output_columns || []).join(' ');
        return [da.name, da.attribute_type, da.role, cols]
          .some((f) => (f || '').toLowerCase().includes(q));
      });
    },
  },

  mounted() {
    this.fetchAttributes();
  },

  methods: {
    async fetchAttributes() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(ATTR_BASE);
        this.attributes = Array.isArray(data) ? data : (data.results || []);
      } catch {
        this.attributes = [];
      } finally {
        this.isLoading = false;
      }
    },

    onCreated(da) {
      this.successMessage = `Data attribute “${da.name}” created.`;
      this.fetchAttributes();
    },
  },
};
</script>
